'use client';

import { Box } from '@mui/material';
import { Ilr2StructuredImgCell } from '@/components/publicSearch/Ilr2StructuredImgCell';
import { parseMixedIlr2Html } from '@/utils/stripHtmlForTooltip';

type Ilr2MixedHtmlLabelProps = {
  raw: string | null | undefined;
  societyId: string | number | undefined;
  /** Receives stripped plain text (never raw HTML). */
  children: (plainText: string) => React.ReactNode;
  badgeAlt?: string;
  /**
   * Keep text + badge on one line. Opt in for the fixed-height search-grid
   * cells that ellipsise; leave false for long free-form captions that wrap.
   */
  nowrap?: boolean;
};

/**
 * Renders ILR2 mixed markup: plain text (via `children`) plus an optional
 * img badge (HERF Super Sire / Former Super Sire, completeness icon, etc.).
 */
export function Ilr2MixedHtmlLabel({
  raw,
  societyId,
  children,
  badgeAlt = 'Badge',
  nowrap = false,
}: Ilr2MixedHtmlLabelProps) {
  const { plainText, imgMarkup } = parseMixedIlr2Html(raw);
  const showBadge = Boolean(imgMarkup && societyId);

  // Still render children when empty so callers can apply identifier / ID fallbacks.
  if (!plainText && !showBadge) {
    return <>{children(plainText)}</>;
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        maxWidth: '100%',
        flexWrap: nowrap ? 'nowrap' : 'wrap',
        whiteSpace: nowrap ? 'nowrap' : undefined,
      }}
    >
      {children(plainText)}
      {showBadge ? (
        <Ilr2StructuredImgCell
          value={imgMarkup!}
          societyId={societyId!}
          alt={badgeAlt}
        />
      ) : null}
    </Box>
  );
}
